import { AppstoreAddOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AutoplayModuleSpecsFragment } from '../../../../api/fragments/AutoplayModuleSpecs';
import useDeleteModule from '../useDeleteModule';
import { useAutoplaySettingsDrawer } from './AutoplaySettingsDrawer';
import { useUpdateAutoplayModuleDrawer } from './UpdateAutoplayModuleDrawer';

export type AutoplayModuleActionsProps = {
    module: AutoplayModuleSpecsFragment;
};

const AutoplayModuleActions = ({ module }: AutoplayModuleActionsProps) => {
    const { t } = useTranslation(['autoplayModuleDetails', 'moduleDetails']);
    const deleteModule = useDeleteModule(module.id);
    const addAutoplaySettingsDrawer = useAutoplaySettingsDrawer(module.id);

    const initialValues = useMemo(() => pick(['displayName'], module), [module]);
    const updateAutoplayModuleDrawer = useUpdateAutoplayModuleDrawer(module.id, initialValues);

    const items = useMemo(
        () => [
            {
                label: t('autoplayModuleDetails:mainActionButtons.updateSpecs'),
                key: 'updateSpecs',
                icon: <EditOutlined />,
                onClick: updateAutoplayModuleDrawer.open,
            },
            {
                label: t('autoplayModuleDetails:mainActionButtons.addSetting'),
                key: 'addSetting',
                icon: <AppstoreAddOutlined />,
                onClick: addAutoplaySettingsDrawer.open,
            },
            {
                label: t('autoplayModuleDetails:mainActionButtons.delete'),
                key: 'delete',
                icon: <DeleteOutlined />,
                onClick: deleteModule,
                danger: true,
            },
        ],
        [addAutoplaySettingsDrawer.open, deleteModule, t, updateAutoplayModuleDrawer.open]
    );

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('autoplayModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {addAutoplaySettingsDrawer.render()}
            {updateAutoplayModuleDrawer.render()}
        </>
    );
};

export default AutoplayModuleActions;
